import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { ConstructionSitesProperties } from 'src/app/shared/models/construction-sites.model'

@Component({
  selector: 'app-construction-sites-details',
  templateUrl: './construction-sites-details.component.html',
  standalone: false,
})
export class ConstructionSitesDetailsComponent {
  public readonly selectedFeatureId = signal('')
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly zoomLevel = signal<number | undefined>(undefined)
  public readonly index = signal(0)

  constructor(
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        if (this.features().length != features.length) {
          this.index.set(0)
        }
        if (features[this.index()].layer.id === `${Layers.ConstructionSites.name}-symbols`) {
          this.features.set(features)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    if (
      this.selectedFeatureId() &&
      this.selectedFeatureId() == this.features()[this.index()].id?.toString()
    ) {
      this.selectedFeatureId.set('')
      this.close()
      return
    }
    this.selectedFeatureId.set(this.features()[this.index()].id?.toString() ?? '')
    const properties: ConstructionSitesProperties = this.features()[this.index()]
      .properties as ConstructionSitesProperties
    this.zoomLevel.set(this.appState.getState().zoom)
    this.mapConstructionSitesToDetails(properties)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: this.selectedFeatureId(),
    })
  }

  mapConstructionSitesToDetails(properties: ConstructionSitesProperties) {
    const rawMetric = {
      title: 'Construction Site',
      items: [
        {
          description: 'ID',
          value: properties.id,
        },
        {
          description: 'Direction',
          value: properties.direction,
        },
        {
          description: 'Country',
          value: properties.country_code,
        },
        {
          description: 'Way ID',
          value: properties.way_id,
        },
        {
          description: 'Probability',
          value: String(properties.probability),
        },
        {
          description: properties.update_time
            ? `Updated(${getTimeZoneLabel(new Date(properties.update_time))})`
            : 'Updated',
          value: properties.update_time
            ? format(new Date(properties.update_time), 'yyyy-MM-dd HH:mm')
            : '-',
        },
        {
          description: properties.expiry_time
            ? `Expires(${getTimeZoneLabel(new Date(properties.expiry_time))})`
            : 'Expires',
          value: properties.expiry_time
            ? format(new Date(properties.expiry_time), 'yyyy-MM-dd HH:mm')
            : '-',
        },
        {
          description: properties.creation_time
            ? `Created(${getTimeZoneLabel(new Date(properties.creation_time))})`
            : 'Created',
          value: properties.creation_time
            ? format(new Date(properties.creation_time), 'yyyy-MM-dd HH:mm')
            : '-',
        },
        {
          description: properties.matched_at
            ? `Matched(${getTimeZoneLabel(new Date(properties.matched_at))})`
            : 'Matched',
          value: properties.matched_at
            ? format(new Date(properties.matched_at), 'yyyy-MM-dd HH:mm')
            : '-',
        },
      ],
    }

    this.setDetail('RawMetric', rawMetric)
  }

  close() {
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: undefined,
    })
    this.selectedFeatureId.set('')
    this.clearDetails()
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
